
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DateRange } from "react-day-picker";

interface FilterOptions {
  search: string;
  status: string;
  country: string;
  dateRange?: DateRange;
  groupByUser?: boolean;
}

export interface DocumentProps {
  id: string;
  user_id: string;
  document_type_id: string;
  document_number?: string;
  issue_date?: string;
  expiry_date?: string;
  file_url: string;
  submitted_at: string;
  verification_status: "pending" | "verified" | "rejected";
  primary_document: boolean;
  email: string;
  profiles: {
    first_name: string;
    last_name: string;
    country_code: string;
  };
  id_document_types: {
    document_name: string;
    country_code: string;
  };
  verified_by?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UserWithDocuments {
  user_id: string;
  name: string;
  email: string;
  documents: DocumentProps[];
}

export const useSubmittedDocuments = (filter: FilterOptions) => {
  const query = useQuery({
    queryKey: ['submitted-documents', filter],
    queryFn: async () => {
      // Add debug log
      console.log("Fetching documents with filter:", filter);
      
      const { data: documents, error: documentsError } = await supabase
        .from('user_documents_with_info')
        .select('*');

      if (documentsError) throw documentsError;
      
      if (!documents) return { documents: [], userGroups: [] };

      // Add debug log to see raw document data
      console.log("Raw documents from API:", documents);

      const processedData = documents.map(doc => ({
        ...doc,
        profiles: {
          first_name: doc.first_name || 'Unknown',
          last_name: doc.last_name || 'User',
          country_code: doc.country_code || 'Unknown'
        },
        id_document_types: {
          document_name: doc.document_name || 'Unknown Document',
          country_code: doc.country_code || 'Unknown'
        }
      })) as DocumentProps[];

      const filteredDocuments = processedData.filter(doc => {
        const matchesSearch = filter.search
          ? `${doc.profiles.first_name} ${doc.profiles.last_name}`
              .toLowerCase()
              .includes(filter.search.toLowerCase()) ||
            doc.email?.toLowerCase().includes(filter.search.toLowerCase()) ||
            doc.document_number?.toLowerCase().includes(filter.search.toLowerCase())
          : true;

        const matchesStatus = filter.status === 'all' 
          ? true 
          : doc.verification_status === filter.status;

        const matchesCountry = filter.country === 'all'
          ? true
          : doc.id_document_types.country_code === filter.country;

        const matchesDateRange = !filter.dateRange?.from
          ? true
          : new Date(doc.submitted_at) >= filter.dateRange.from &&
            (!filter.dateRange.to || new Date(doc.submitted_at) <= filter.dateRange.to);

        return matchesSearch && matchesStatus && matchesCountry && matchesDateRange;
      });

      // Group documents by user if requested
      if (filter.groupByUser) {
        const userMap = new Map<string, UserWithDocuments>();
        
        filteredDocuments.forEach(doc => {
          if (!userMap.has(doc.user_id)) {
            userMap.set(doc.user_id, {
              user_id: doc.user_id,
              name: `${doc.profiles.first_name} ${doc.profiles.last_name}`,
              email: doc.email || '',
              documents: []
            });
          }
          userMap.get(doc.user_id)?.documents.push(doc);
        });
        
        // Sort documents within each user group by date (newest first)
        const userGroups = Array.from(userMap.values()).map(user => {
          return {
            ...user,
            documents: user.documents.sort((a, b) => 
              new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
            )
          };
        });
        
        // Sort users by most recent document
        userGroups.sort((a, b) => {
          const aDate = new Date(a.documents[0]?.submitted_at || 0).getTime();
          const bDate = new Date(b.documents[0]?.submitted_at || 0).getTime();
          return bDate - aDate;
        });
        
        return { 
          documents: filteredDocuments,
          userGroups: Array.from(userMap.values())
        };
      }
      
      return { 
        documents: filteredDocuments,
        userGroups: []
      };
    }
  });

  return {
    documents: query.data?.documents || [],
    userGroups: query.data?.userGroups || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
};
